import {
  type ConvictionMarketFields,
  type MarketConfig,
  outcomeConvictionFraction,
  type SimilarMarketsByMarketRow,
} from '@morfi/backend-sdk-v2/client';
import type { ProposalScore } from '@/components/launchpad/proposal-score-badge';
import type { ProposalListItem } from '@/components/launchpad/types';
import type { OutcomeRowSummary } from '@/components/markets/outcome-row-summary';
import type { TopBackerSummary } from '@/lib/launchpad/derive-fake-fields';

type SimilarMarket = SimilarMarketsByMarketRow['markets'][number];
type SimilarProposal = SimilarMarketsByMarketRow['proposals'][number];

export interface AdaptedMarket {
  readonly pubkey: string;
  readonly title: string;
  readonly coverImageUrl: string;
  readonly coverImageNsfw: boolean;
  readonly location: string | null;
  readonly category: string | null;
  readonly volumeTotal: string;
  readonly prizePool: string;
  readonly outcomes: OutcomeRowSummary[];
  readonly consensusThreshold: string | null;
  readonly uniqueTradersTotal: number;
}

export function adaptMarket(m: SimilarMarket, config: MarketConfig | undefined): AdaptedMarket {
  const outcomes = Array.isArray(m.outcomes) ? m.outcomes : [];
  // RAW conviction (resolution-aware) via the SDK — never a normalised share.
  const market: ConvictionMarketFields = {
    status: m.status,
    resolvedAt: m.resolvedAt,
    convictionUpdatedAt: m.convictionUpdatedAt,
    outcomeCount: m.outcomeCount,
  };
  const outcomeSummaries: OutcomeRowSummary[] = outcomes.map((o) => ({
    label: o.label,
    share: outcomeConvictionFraction(o, market),
    originalIndex: o.outcomeIndex,
    effectiveMarketCapD6: o.effectiveMarketCapD6,
    imageUrl: o.imageUrl,
    imageNsfw: o.imageNsfw,
  }));
  return {
    pubkey: m.pubkey,
    title: m.title,
    coverImageUrl: m.coverImageUrl ?? '',
    coverImageNsfw: m.coverImageNsfw === true,
    location: m.location !== '' && m.location !== null ? m.location : null,
    category: m.category !== '' && m.category !== null ? m.category : null,
    volumeTotal: m.volumeTotal ?? '0',
    prizePool: m.prizePool ?? '0',
    outcomes: outcomeSummaries,
    consensusThreshold: config?.convictionResolutionThresholdPpb ?? null,
    uniqueTradersTotal: m.uniqueTradersTotal ?? 0,
  };
}

export interface AdaptedProposalView {
  readonly proposal: ProposalListItem;
  readonly score: ProposalScore;
  readonly topBackers: TopBackerSummary[];
}

export function adaptProposal(
  p: SimilarProposal,
  config: MarketConfig | undefined,
): AdaptedProposalView {
  const proposer = p.proposer;
  const proposal: ProposalListItem = {
    marketType: p.marketType,
    partnerCreatorId: p.partnerCreatorId,
    associatedCreator: p.associatedCreator,
    pubkey: p.pubkey ?? '',
    title: p.title ?? '',
    description: p.description ?? '',
    category: p.category ?? '',
    location: p.location ?? null,
    status: p.status ?? '',
    totalFunded: p.totalFunded ?? '0',
    totalStaked: p.totalStaked,
    backerCount: p.backerCount ?? 0,
    createdAt: p.createdAt ?? '',
    updatedAt: p.updatedAt ?? '',
    expiresAt: p.expiresAt ?? null,
    creator: proposer?.walletAddress ?? '',
    proposerDisplayName: proposer?.displayName ?? null,
    proposerHandle: proposer?.handle ?? null,
    proposerAvatarUrl: proposer?.avatarUrl ?? null,
    proposerAvatarNsfw: proposer?.avatarNsfw === true,
    proposerAvatarPreferences: proposer?.avatarPreferences ?? null,
    proposerConnectedProviders: [...(proposer?.connectedProviders ?? [])],
    coverImageUrl: p.coverImageUrl ?? null,
    coverImageNsfw: p.coverImageNsfw === true,
    numOutcomes: p.numOutcomes ?? 0,
    outcomes: Array.isArray(p.outcomes)
      ? p.outcomes.map((o) => ({
          outcomeIndex: o.outcomeIndex,
          label: o.label,
          imageUrl: o.imageUrl ?? '',
          imageNsfw: o.imageNsfw === true,
          funded: o.funded,
          fundedPercent: o.fundedPercent,
        }))
      : [],
    debateSpreadOk: p.debateSpreadOk ?? false,
    creationThreshold: config?.creationThresholdD6 ?? null,
    graduatedMarketPubkey: p.graduatedMarketPubkey ?? null,
    rejectionReason: null,
  };
  const topBackers: TopBackerSummary[] = Array.isArray(p.topBackers)
    ? p.topBackers.map((b) => ({
        walletAddress: b.backer?.walletAddress ?? '',
        displayName: b.backer?.displayName ?? null,
        avatarUrl: b.backer?.avatarUrl ?? null,
        avatarNsfw: b.backer?.avatarNsfw === true,
      }))
    : [];
  return {
    proposal,
    score: (p.proposalScore ?? 'medium') as ProposalScore,
    topBackers,
  };
}
